import React, { useCallback, useEffect, useState } from 'react';
import { BarChart3, RefreshCw } from 'lucide-react';
import {
  loadCrewJobsAnalyticsSummary,
  type CrewJobsAnalyticsSummary,
} from '../../../utils/crewJobsAnalytics';

const DAY_OPTIONS = [7, 30, 90] as const;

type Props = {
  /** Compact body for the Crew jobs header hover popover. */
  compact?: boolean;
};

const CrewJobsAnalyticsPanel: React.FC<Props> = ({ compact = false }) => {
  const [days, setDays] = useState<(typeof DAY_OPTIONS)[number]>(30);
  const [summary, setSummary] = useState<CrewJobsAnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (windowDays: number) => {
    setLoading(true);
    setError(null);
    try {
      const next = await loadCrewJobsAnalyticsSummary(windowDays);
      setSummary(next);
    } catch (e) {
      setSummary(null);
      setError((e as Error).message || 'Could not load board analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh(days);
  }, [days, refresh]);

  const controls = (
    <div className="people-hiring-crew-analytics-controls">
      <label className="people-hiring-muted">
        Last
        <select
          value={days}
          onChange={e => setDays(Number(e.target.value) as (typeof DAY_OPTIONS)[number])}
          aria-label="Analytics window"
        >
          {DAY_OPTIONS.map(d => (
            <option key={d} value={d}>
              {d} days
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        className="pm-btn pm-btn-ghost"
        onClick={() => void refresh(days)}
        disabled={loading}
        aria-label="Refresh analytics"
      >
        <RefreshCw size={14} />
      </button>
    </div>
  );

  const body = (
    <>
      <p className="people-hiring-muted" style={{ marginBottom: 10 }}>
        First-party views for <code>/crew-jobs</code>. Anonymous visitor ids only — no names or emails.
      </p>

      {loading && <p className="people-hiring-muted">Loading…</p>}
      {error && <p className="people-hiring-resume-error">{error}</p>}

      {!loading && !error && summary && (
        <>
          <div className="people-hiring-analytics-stats">
            <div>
              <strong>{summary.pageViews}</strong>
              <span>Page views</span>
            </div>
            <div>
              <strong>{summary.uniqueVisitors}</strong>
              <span>Unique visitors</span>
            </div>
            <div>
              <strong>{summary.jobViews}</strong>
              <span>Job views</span>
            </div>
            <div>
              <strong>{summary.cardClicks}</strong>
              <span>Card clicks</span>
            </div>
            <div>
              <strong>{summary.commitClicks}</strong>
              <span>Commit CTA</span>
            </div>
            <div>
              <strong>{summary.commitSubmits}</strong>
              <span>Commit submits</span>
            </div>
          </div>

          <div className="people-hiring-analytics-cols">
            <div>
              <h4 className="people-hiring-analytics-subhead">Top raises</h4>
              {summary.topJobs.length === 0 ? (
                <p className="people-hiring-muted">No job engagement yet.</p>
              ) : (
                <div className="people-hiring-table-wrap">
                  <table className="people-hiring-table">
                    <thead>
                      <tr>
                        <th>Raise</th>
                        <th>Views</th>
                        <th>Cards</th>
                        <th>CTA</th>
                        <th>Submit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.topJobs.map(job => (
                        <tr key={job.key}>
                          <td>
                            <strong>{job.jobTitle || job.jobSlug || job.jobId || '—'}</strong>
                            {job.jobSlug ? (
                              <div className="people-hiring-muted">{job.jobSlug}</div>
                            ) : null}
                          </td>
                          <td>{job.views}</td>
                          <td>{job.cardClicks}</td>
                          <td>{job.commitClicks}</td>
                          <td>{job.commitSubmits}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div>
              <h4 className="people-hiring-analytics-subhead">Attribution</h4>
              {summary.attribution.length === 0 ? (
                <p className="people-hiring-muted">No events yet.</p>
              ) : (
                <ul className="people-hiring-analytics-attr">
                  {summary.attribution.map(row => (
                    <li key={row.label}>
                      <span>{row.label}</span>
                      <strong>{row.count}</strong>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );

  if (compact) {
    return (
      <div className="people-hiring-crew-analytics people-hiring-crew-analytics--popover" aria-label="Crew board analytics">
        <div className="people-hiring-section-head">
          <h3>Board traffic</h3>
          {controls}
        </div>
        {body}
      </div>
    );
  }

  return (
    <section className="people-hub-panel people-hiring-crew-analytics" aria-label="Crew board analytics">
      <div className="people-hiring-section-head">
        <h3>
          <BarChart3 size={16} aria-hidden style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
          Board traffic
        </h3>
        {controls}
      </div>
      {body}
    </section>
  );
};

export default CrewJobsAnalyticsPanel;
